<template>
  <div class="sizes">
    <div class="size">
      <b-field :label="labelName('width')" class="is-marginless">
        <b-field class="is-marginless">
          <b-input
            v-model="tempModel.width"
            inputmode="decimal"
            class="number-size"
            :class="[{ 'solo-size': tempModel.type !== 'inch' }]"
            :aria-label="labelName('width')"
            @blur="change"
            @focus="$event.target.select()"
            @keydown.enter.native.prevent="change"
          />
          <b-autocomplete
            v-show="tempModel.type === 'inch'"
            v-model="tempModel.widthFraction"
            class="decimal-fraction"
            :data="filteredFraction(tempModel.widthFraction)"
            open-on-focus
            @focus="$event.target.select()"
            @blur="change()"
            @keydown.enter.native.prevent="change()"
            @select="x => selected('widthFraction', x)"
          />
        </b-field>
      </b-field>
    </div>

    <div class="s-x">
      X
    </div>

    <div class="size">
      <b-field :label="labelName('height')" class="is-marginless">
        <b-field class="is-marginless">
          <b-input
            v-model="tempModel.height"
            inputmode="decimal"
            class="number-size"
            :class="[{ 'solo-size': tempModel.type !== 'inch' }]"
            :aria-label="labelName('height')"
            @blur="change"
            @focus="$event.target.select()"
            @keydown.enter.native.prevent="change"
          />
          <b-autocomplete
            v-show="tempModel.type === 'inch'"
            v-model="tempModel.heightFraction"
            class="decimal-fraction"
            :data="filteredFraction(tempModel.heightFraction)"
            open-on-focus
            @focus="$event.target.select()"
            @blur="change()"
            @keydown.enter.native.prevent="change()"
            @select="x => selected('heightFraction', x)"
          />
        </b-field>
      </b-field>
    </div>

    <div v-if="filteredUnits.length > 1" class="unit">
      <b-select v-model="tempModel.type" @change.native="changeType">
        <option v-for="item in filteredUnits" :key="item" :value="item">
          {{ $t(`fields.${item}`) }}
        </option>
      </b-select>
    </div>
  </div>
</template>

<script>
import toFraction from 'marketplace-front-core/utils/toFraction'
export default {
  props: {
    size: {
      type: Object,
      default: null
    },
    product: {
      type: Object,
      default: null
    }
  },
  data () {
    return {
      apiStructure: {},
      model: {
        width: null,
        height: null,
        type: null
      },
      tempModel: {
        width: null,
        height: null,
        type: null,
        widthFraction: null,
        heightFraction: null
      },
      lastModel: null,
      convertCmInch: 2.54,
      fractions: [
        '0/0',
        '1/8',
        '1/4',
        '3/8',
        '1/2',
        '5/8',
        '3/4',
        '7/8'
      ],
      LOG_BASE: 7
    }
  },
  computed: {
    numberSize () {
      return this.tempModel.width ? this.tempModel.width.toString().length : 1
    },
    getSizeStructure () {
      if (this.model && (parseInt(this.tempModel.width) >= 0 || parseInt(this.tempModel.height) >= 0)) {
        const sizes = {
          width: `${this.tempModel.width}${this.tempModel.widthFraction && this.tempModel.widthFraction !== '0/0' ? ' ' + this.tempModel.widthFraction : ''}`,
          height: `${this.tempModel.height}${this.tempModel.heightFraction && this.tempModel.heightFraction !== '0/0' ? ' ' + this.tempModel.heightFraction : ''}`,
          type: this.tempModel.type
        }
        return { ...this.apiStructure, ...sizes }
      } else {
        return null
      }
    },
    filteredUnits () {
      const sizeUnit = JSON.parse(process.env.sizeUnit)
      const defaultSizeUnit = process.env.defaultSizeUnit
      return sizeUnit?.[this.currentLang]?.split('|') || [defaultSizeUnit]
    }
  },
  watch: {
    'tempModel.width' (value) {
      this.$nextTick(() => {
        this.tempModel.width = this.replaceSize(value, this.tempModel.type)
        this.$emit('update:size', this.getSizeStructure)
      })
    },
    'tempModel.height' (value) {
      this.$nextTick(() => {
        this.tempModel.height = this.replaceSize(value, this.tempModel.type)
        this.$emit('update:size', this.getSizeStructure)
      })
    },
    'tempModel.widthFraction' (value) {
      this.$nextTick(() => {
        this.tempModel.widthFraction = this.replaceSize(value, 'inchFraction')
        this.$emit('update:size', this.getSizeStructure)
      })
    },
    'tempModel.heightFraction' (value) {
      this.$nextTick(() => {
        this.tempModel.heightFraction = this.replaceSize(value, 'inchFraction')
        this.$emit('update:size', this.getSizeStructure)
      })
    }
  },
  created () {
    this.setModel(this.size)
    this.$nextTick(() => {
      this.lastModel = this._clone(this.getSizeStructure)
    })
  },
  methods: {
    selected (model, value) {
      if (value) {
        this.tempModel[model] = value
        this.change()
      }
    },
    labelName (size) {
      return this.filteredUnits.length > 1 ? this.$t(`fields.${size}`) : `${this.$t(`fields.${size}`)} (${this.$t(`fields.${this.filteredUnits[0]}`)})`
    },
    changeType () {
      switch (this.tempModel.type) {
        case 'inch':
          // Zamiana z cm na inch
          this.changeCmToInch()
          break
        case 'cm':
          // Zamiana z inch na cm
          this.changeInchToCm()
          break
      }

      this.lastModel = this._clone(this.getSizeStructure)
    },
    filteredFraction (text) {
      const matching = this.fractions.filter((option) => {
        return option
          .toString()
          .toLowerCase()
          .includes(text?.toLowerCase() || '')
      })

      const notMatching = this.fractions.filter((option) => {
        return !matching.includes(option)
      })

      return [...matching, ...notMatching]
    },
    changeCmToInch () {
      const newWeigth = Number(this.tempModel.width) / this.convertCmInch
      const newWeigthFraction = toFraction(newWeigth - Math.trunc(newWeigth))
      const newHeight = Number(this.tempModel.height) / this.convertCmInch
      const newHeightFraction = toFraction(newHeight - Math.trunc(newHeight))

      this.tempModel = {
        width: Math.trunc(newWeigth),
        height: Math.trunc(newHeight),
        type: 'inch',
        widthFraction: `${newWeigthFraction.fraction}/${newWeigthFraction.denominator}`,
        heightFraction: `${newHeightFraction.fraction}/${newHeightFraction.denominator}`
      }
    },
    changeInchToCm () {
      const widthFraction = this.tempModel.widthFraction?.split('/') || [0]
      const newWeigth = ((Number(this.tempModel.width) || 0) + ((Number(widthFraction[0]) || 0) / (Number(widthFraction[1]) || 0))) * this.convertCmInch

      const heigthFraction = this.tempModel.widthFraction?.split('/') || [0]
      const newHeight = ((Number(this.tempModel.height) || 0) + ((Number(heigthFraction[0]) || 0) / (Number(heigthFraction[1]) || 0))) * this.convertCmInch

      this.tempModel = {
        width: newWeigth.toFixed(1),
        height: newHeight.toFixed(1),
        type: 'cm',
        widthFraction: null,
        heightFraction: null
      }
    },
    setModel (val) {
      if (val) {
        this.setModelData(val)
      } else {
        this.setModelData()
      }
    },
    setModelData (data) {
      this.apiStructure = data || {}
      this.$nextTick(() => {

        this.tempModel.type = data?.type || this.filteredUnits[0]

        if (data?.width) {
          this.tempModel.width = this.getFraction(data.width).number
          this.tempModel.widthFraction = this.getFraction(data.width).fraction || this.fractions[0]
        } else {
          this.tempModel.width = null
          this.tempModel.widthFraction = this.fractions[0]
        }

        if (data?.height) {
          this.tempModel.height = this.getFraction(data.height).number
          this.tempModel.heightFraction = this.getFraction(data.height).fraction || this.fractions[0]
        } else {
          this.tempModel.height = null
          this.tempModel.heightFraction = this.fractions[0]
        }

        this.$emit('update:size', this.getSizeStructure)
      })
    },
    getFraction (value) {
      const regexTest = /^[0-9]{1,}(,[0-9]{1,2})?(\.[0-9]{1,2})? [0-9]{1,}\/[0-9]{1,}/g
      const regexNumber = /^[0-9]{1,}(,[0-9]{1,2})?(\.[0-9]{1,2})?/g
      const regexFraction = /[0-9]{1,}\/[0-9]{1,}$/g
      if (regexTest.test(value)) {
        return {
          number: value.match(regexNumber)[0] || 0,
          fraction: value.match(regexFraction)[0] || this.fractions[0]
        }
      } else {
        return {
          number: value,
          fraction: null
        }
      }
    },
    change () {
      if (!/^[0-9]{1,2}\/[0-9]{1,2}$/.test(this.tempModel.widthFraction)) {
        this.tempModel.widthFraction = this.fractions[0]
      }

      if (!/^[0-9]{1,2}\/[0-9]{1,2}$/.test(this.tempModel.heightFraction)) {
        this.tempModel.heightFraction = this.fractions[0]
      }
      this.$emit('update:size', this.getSizeStructure)

      if (JSON.stringify(this.lastModel) !== JSON.stringify(this.getSizeStructure)) {
        this.$emit('change', true)
        this.eventCustomizeProduct(this.product, this.size)
        this.lastModel = this._clone(this.getSizeStructure)
      }
    },
    replaceSize (value, key) {
      switch (key) {
        case 'cm':
          return value.toString()
            .replace(/,/g, '.')
            .replace(/\./, '-')
            .replace(/\./g, '')
            .replace(/-/g, '.')
            .replace(/[^0-9.]/, '')
            .replace(/^(\d+.?\d{0,1})\d*$/, '$1')
        case 'inch':
          return value.toString()
            .replace(/\./g, '')
            .replace(/[^0-9]/, '')
        case 'inchFraction':
          return value
            ? value.toString()
              .replace(/[^0-9/]/, '')
              .replace(/^([0-9]{0,2}(\/[0-9]{0,2}|\/?))(.*)$/, '$1')
              .replace(/\/\//, '/')
            : null
        default:
          return value.toString()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
  .sizes {
    display: flex;
    align-items: flex-end;
    margin-bottom: 1.25rem;
    .s-x {
      font-weight: bold;
      margin-bottom: 8px;
      margin-left: 6px;
      margin-right: 6px;
    }
    .size {
      width: 180px;
      & > .field {
        margin-bottom: 0;
      }
      @include mobile {
        width: 100%;
        max-width: 100%;
      }
      &:not(:last-child()) {
        margin-right: 10px;
      }
      .temp-model-width {
        width: auto;
        display: inline-block;
        visibility: hidden;
        position: fixed;
        overflow:auto;
      }
      .number-size {
        width: 100%;
      }
      .number-size :deep(.input) {
        position: relative;
      }
      .number-size.solo-size :deep(.input) {
        border-radius: 4px;
      }
      .decimal-fraction :deep(.input) {
        width: calc(5ch + 1.5em);
        padding: 0 0.75em !important;
      }
    }
    .unit {
      width: 75px;
      &>:deep(.control) {
        .select {
          select {
            padding-right: 25px;
          }
          &::after {
            border-color: #393939 !important;
            right: 10px;
          }
        }
      }
    }
  }
</style>
